export type AppleDeviceType = 'IPHONE' | 'IPAD' | 'IPOD' | 'MAC';

export interface AppleDeviceInfo {
  isAppleDevice: boolean;
  type: AppleDeviceType | null;
  isIOS: boolean;
  isMac: boolean;
  isIPad: boolean;
  version: string | null;
  userAgent: string;
}

/**
 * Comprehensive Apple device detection function
 */
export const detectAppleDevice = (): AppleDeviceInfo => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      isAppleDevice: false,
      type: null,
      isIOS: false,
      isMac: false,
      isIPad: false,
      version: null,
      userAgent: '',
    };
  }

  const userAgent = navigator.userAgent || '';
  const maxTouchPoints = navigator.maxTouchPoints ?? 0;
  const isMacintosh = /Macintosh/i.test(userAgent);
  const isIPadUA = /iPad/i.test(userAgent);
  const isIPhoneUA = /iPhone/i.test(userAgent);
  const isIPodUA = /iPod/i.test(userAgent);

  // iPadOS 13+ reports as Mac; touch-capable MacIntel indicates iPad
  const isIPad = isIPadUA || (isMacintosh && maxTouchPoints > 0 && maxTouchPoints < 6);
  const isIPhone = isIPhoneUA;
  const isIPod = isIPodUA;
  const isIOS = isIPhone || isIPad || isIPod;
  const isMac = isMacintosh && !isIPad && maxTouchPoints === 0;

  let type: AppleDeviceInfo['type'] = null;
  if (isIPhone) {
    type = 'IPHONE';
  } else if (isIPad) {
    type = 'IPAD';
  } else if (isIPod) {
    type = 'IPOD';
  } else if (isMac) {
    type = 'MAC';
  }

  let version: string | null = null;
  if (isIOS) {
    const iosMatch = userAgent.match(/OS (\d+[_.\d]*)/i);
    version = iosMatch?.[1]?.replace(/_/g, '.') ?? null;
  } else if (isMac) {
    const macMatch = userAgent.match(/Mac OS X (\d+[_.\d]*)/i);
    version = macMatch?.[1]?.replace(/_/g, '.') ?? null;
  }

  return {
    isAppleDevice: type !== null,
    type,
    isIOS,
    isMac,
    isIPad,
    version,
    userAgent,
  };
};

/**
 * Simple boolean check for Apple devices (iPhone, iPad, iPod, Mac)
 */
export const isAppleDevice = (): boolean => {
  return detectAppleDevice().isAppleDevice;
};
